import { useState } from 'react';
import { Settings, BarChart3, FileText, GraduationCap, Award, Plus, Trash2, Menu, X, ArrowLeft, Upload, Download, Calendar, Edit2, Users, Building2, Mail, Phone, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../ui/card';
import chepLogo from '../../assets/images/CHEP_Logo.png';
import { Customer } from './CustomerSelection';

interface AdminPanelProps {
    onUpdateGraphData: (data: any[]) => void;
    onUpdateCertificates: (certificates: any[]) => void;
    onUpdateCEUniversity: (data: any) => void;
    onUpdateSuppliers?: (suppliers: any) => void;
    onBackToCustomers: () => void;
    currentGraphData: any[];
    currentCertificates: any[];
    currentCEData: any;
    currentSuppliers?: any;
    selectedCustomer: Customer;
}

type Section = 'graph' | 'certificates' | 'ce-university' | 'download-cert' | 'suppliers';

export function AdminPanel({
    onUpdateGraphData,
    onUpdateCertificates,
    onUpdateCEUniversity,
    onUpdateSuppliers,
    onBackToCustomers,
    currentGraphData,
    currentCertificates,
    currentCEData,
    currentSuppliers,
    selectedCustomer,
}: AdminPanelProps) {
    const [activeSection, setActiveSection] = useState<Section>('graph');
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    // Graph Data State
    const [graphData, setGraphData] = useState(currentGraphData);

    // Certificates State
    const [certificates, setCertificates] = useState(currentCertificates);
    const [newCertificate, setNewCertificate] = useState({
        title: '',
        description: '',
        date: '',
    });

    // CE University State
    const [ceData, setCEData] = useState(currentCEData);
    const [ceCategory, setCECategory] = useState<'caseStudies' | 'videos' | 'webinars'>('caseStudies');
    const [newCEItem, setNewCEItem] = useState({
        title: '',
        description: '',
        duration: '',
        category: '',
    });

    // Suppliers State
    interface Supplier {
        id: number;
        name: string;
        contact: string;
        email: string;
        phone: string;
        status: 'integrated' | 'non-integrated';
        co2eImpact: number;
        wasteImpact: number;
        treesImpact: number;
    }

    const [suppliers, setSuppliers] = useState<Supplier[]>(currentSuppliers || []);
    const [newSupplier, setNewSupplier] = useState({
        name: '',
        contact: '',
        email: '',
        phone: '',
        status: 'integrated' as 'integrated' | 'non-integrated',
        co2eImpact: 0,
        wasteImpact: 0,
        treesImpact: 0,
    });

    // Save all changes
    const handleSaveAll = () => {
        onUpdateGraphData(graphData);
        onUpdateCertificates(certificates);
        onUpdateCEUniversity(ceData);
        if (onUpdateSuppliers) {
            onUpdateSuppliers(suppliers);
        }
        alert('All changes saved successfully!');
    };

    // Graph Data Handlers
    const handleUpdateGraphItem = (id: string, field: string, value: number | string) => {
        setGraphData((prev: any[]) =>
            prev.map(item =>
                item.id === id ? { ...item, [field]: value } : item
            )
        );
    };

    // Year-over-year data handlers
    const handleUpdateYearData = (id: string, year: 'previousYear' | 'currentYear', metric: string, value: number) => {
        setGraphData((prev: any[]) =>
            prev.map(item => {
                if (item.id === id) {
                    const updatedItem = {
                        ...item,
                        [year]: {
                            ...(item[year] || {}),
                            [metric]: value
                        }
                    };

                    // Auto-calculate the difference
                    if (updatedItem.previousYear && updatedItem.currentYear) {
                        updatedItem.co2e = updatedItem.currentYear.co2e - updatedItem.previousYear.co2e;
                        updatedItem.waste = updatedItem.currentYear.waste - updatedItem.previousYear.waste;
                        updatedItem.treesSaved = updatedItem.currentYear.treesSaved - updatedItem.previousYear.treesSaved;
                    }

                    return updatedItem;
                }
                return item;
            })
        );
    };

    // Certificate Handlers
    const handleAddCertificate = () => {
        if (newCertificate.title && newCertificate.description) {
            const certificate = {
                id: Date.now(),
                ...newCertificate,
            };
            setCertificates([...certificates, certificate]);
            setNewCertificate({ title: '', description: '', date: '' });
        }
    };

    const handleDeleteCertificate = (id: number) => {
        setCertificates(certificates.filter(cert => cert.id !== id));
    };

    const handleUpdateCertificate = (id: number, field: string, value: string) => {
        setCertificates(prev =>
            prev.map(cert =>
                cert.id === id ? { ...cert, [field]: value } : cert
            )
        );
    };

    const handleMoveCertificate = (index: number, direction: 'up' | 'down') => {
        const newCertificates = [...certificates];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex >= 0 && targetIndex < certificates.length) {
            [newCertificates[index], newCertificates[targetIndex]] =
                [newCertificates[targetIndex], newCertificates[index]];
            setCertificates(newCertificates);
        }
    };

    // CE University Handlers
    const handleAddCEItem = () => {
        if (newCEItem.title && newCEItem.description) {
            const item = {
                id: Date.now(),
                ...newCEItem,
            };
            setCEData({
                ...ceData,
                [ceCategory]: [...(ceData[ceCategory] || []), item],
            });
            setNewCEItem({ title: '', description: '', duration: '', category: '' });
        }
    };

    const handleDeleteCEItem = (category: string, id: number) => {
        setCEData({
            ...ceData,
            [category]: ceData[category].filter((item: any) => item.id !== id),
        });
    };

    const handleMoveCEItem = (category: string, index: number, direction: 'up' | 'down') => {
        const items = [...ceData[category]];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex >= 0 && targetIndex < items.length) {
            [items[index], items[targetIndex]] = [items[targetIndex], items[index]];
            setCEData({
                ...ceData,
                [category]: items,
            });
        }
    };

    // Suppliers Handlers
    const handleAddSupplier = () => {
        if (newSupplier.name && newSupplier.contact && newSupplier.email && newSupplier.phone) {
            const supplier = {
                id: Date.now(),
                ...newSupplier,
            };
            setSuppliers([...suppliers, supplier]);
            setNewSupplier({
                name: '',
                contact: '',
                email: '',
                phone: '',
                status: 'integrated' as 'integrated' | 'non-integrated',
                co2eImpact: 0,
                wasteImpact: 0,
                treesImpact: 0,
            });
        }
    };

    const handleDeleteSupplier = (id: number) => {
        setSuppliers(suppliers.filter((supplier: Supplier) => supplier.id !== id));
    };

    const handleUpdateSupplier = (id: number, field: string, value: string | number) => {
        setSuppliers((prev: Supplier[]) =>
            prev.map((supplier: Supplier) =>
                supplier.id === id ? { ...supplier, [field]: value } : supplier
            )
        );
    };

    const menuItems = [
        { id: 'graph' as Section, label: 'Graph Data', icon: BarChart3 },
        { id: 'certificates' as Section, label: 'Certificates', icon: FileText },
        { id: 'ce-university' as Section, label: 'CE University', icon: GraduationCap },
        { id: 'download-cert' as Section, label: 'Certificate Preview', icon: Award },
        { id: 'suppliers' as Section, label: 'Suppliers', icon: Users },
    ];

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* Sidebar */}
            <div
                className={`${isSidebarOpen ? 'w-64' : 'w-0'
                    } bg-white border-r border-gray-200 transition-all duration-300 overflow-hidden flex-shrink-0`}
            >
                <div className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-[#0072CE] rounded-full flex items-center justify-center">
                            <span className="text-white text-lg">{selectedCustomer.name.charAt(0)}</span>
                        </div>
                        <div>
                            <div className="text-gray-900">{selectedCustomer.name}</div>
                            <div className="text-xs text-gray-500">{selectedCustomer.industry}</div>
                        </div>
                    </div>
                    <div className="px-3 py-2 bg-blue-50 rounded-lg mb-8">
                        <p className="text-xs text-gray-600">Dashboard Admin</p>
                    </div>

                    <nav className="space-y-2">
                        {menuItems.map((item) => {
                            const Icon = item.icon;
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => setActiveSection(item.id)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${activeSection === item.id
                                        ? 'bg-[#0072CE] text-white shadow-md'
                                        : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                >
                                    <Icon className="w-5 h-5" />
                                    <span className="text-sm">{item.label}</span>
                                </button>
                            );
                        })}
                    </nav>

                    {/* Back to Customers Button */}
                    <div className="mt-8 pt-8 border-t border-gray-200">
                        <button
                            onClick={onBackToCustomers}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-all"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            <span className="text-sm">Back to Customers</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                {isSidebarOpen ? (
                                    <X className="w-5 h-5 text-gray-600" />
                                ) : (
                                    <Menu className="w-5 h-5 text-gray-600" />
                                )}
                            </button>
                            <div className="flex items-center gap-2">
                                <Settings className="w-6 h-6 text-[#0072CE]" />
                                <h2 className="text-xl text-gray-900">
                                    {menuItems.find(item => item.id === activeSection)?.label}
                                </h2>
                            </div>
                        </div>
                        <Button onClick={handleSaveAll} className="gap-2">
                            <Settings className="w-4 h-4" />
                            Save All Changes
                        </Button>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="max-w-6xl mx-auto">
                        {/* Graph Data Section */}
                        {activeSection === 'graph' && (
                            <div className="space-y-6">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Environmental Impact Data Points</CardTitle>
                                        <CardDescription>
                                            Edit the 4 main data points displayed in the circular impact chart
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        {graphData.map((item) => (
                                            <div key={item.id} className="p-4 border border-gray-200 rounded-lg space-y-4">
                                                <h3 className="text-gray-900">{item.name}</h3>

                                                {/* Year-over-year data */}
                                                <div className="grid grid-cols-2 gap-6">
                                                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                                                        <h4 className="text-sm text-gray-700">Previous Year (2024)</h4>
                                                        <div className="space-y-3">
                                                            <div>
                                                                <Label htmlFor={`co2e-prev-${item.id}`}>CO₂e (tCO₂e)</Label>
                                                                <Input
                                                                    id={`co2e-prev-${item.id}`}
                                                                    type="number"
                                                                    value={item.previousYear?.co2e || 0}
                                                                    onChange={(e) =>
                                                                        handleUpdateYearData(item.id, 'previousYear', 'co2e', parseFloat(e.target.value) || 0)
                                                                    }
                                                                    placeholder="0"
                                                                />
                                                            </div>
                                                            <div>
                                                                <Label htmlFor={`waste-prev-${item.id}`}>Waste (kg)</Label>
                                                                <Input
                                                                    id={`waste-prev-${item.id}`}
                                                                    type="number"
                                                                    value={item.previousYear?.waste || 0}
                                                                    onChange={(e) =>
                                                                        handleUpdateYearData(item.id, 'previousYear', 'waste', parseFloat(e.target.value) || 0)
                                                                    }
                                                                    placeholder="0"
                                                                />
                                                            </div>
                                                            <div>
                                                                <Label htmlFor={`trees-prev-${item.id}`}>Trees Saved</Label>
                                                                <Input
                                                                    id={`trees-prev-${item.id}`}
                                                                    type="number"
                                                                    value={item.previousYear?.treesSaved || 0}
                                                                    onChange={(e) =>
                                                                        handleUpdateYearData(item.id, 'previousYear', 'treesSaved', parseFloat(e.target.value) || 0)
                                                                    }
                                                                    placeholder="0"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                                                        <h4 className="text-sm text-gray-700">Current Year (2025)</h4>
                                                        <div className="space-y-3">
                                                            <div>
                                                                <Label htmlFor={`co2e-cur-${item.id}`}>CO₂e (tCO₂e)</Label>
                                                                <Input
                                                                    id={`co2e-cur-${item.id}`}
                                                                    type="number"
                                                                    value={item.currentYear?.co2e || 0}
                                                                    onChange={(e) =>
                                                                        handleUpdateYearData(item.id, 'currentYear', 'co2e', parseFloat(e.target.value) || 0)
                                                                    }
                                                                    placeholder="0"
                                                                />
                                                            </div>
                                                            <div>
                                                                <Label htmlFor={`waste-cur-${item.id}`}>Waste (kg)</Label>
                                                                <Input
                                                                    id={`waste-cur-${item.id}`}
                                                                    type="number"
                                                                    value={item.currentYear?.waste || 0}
                                                                    onChange={(e) =>
                                                                        handleUpdateYearData(item.id, 'currentYear', 'waste', parseFloat(e.target.value) || 0)
                                                                    }
                                                                    placeholder="0"
                                                                />
                                                            </div>
                                                            <div>
                                                                <Label htmlFor={`trees-cur-${item.id}`}>Trees Saved</Label>
                                                                <Input
                                                                    id={`trees-cur-${item.id}`}
                                                                    type="number"
                                                                    value={item.currentYear?.treesSaved || 0}
                                                                    onChange={(e) =>
                                                                        handleUpdateYearData(item.id, 'currentYear', 'treesSaved', parseFloat(e.target.value) || 0)
                                                                    }
                                                                    placeholder="0"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Calculated Impact Display */}
                                                {item.previousYear && item.currentYear && (
                                                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                                                        <h4 className="text-sm text-gray-700 mb-3">Calculated Impact (Auto-Generated)</h4>
                                                        <div className="grid grid-cols-3 gap-4">
                                                            <div className="text-center">
                                                                <p className="text-xs text-gray-600 mb-1">CO₂e Reduction</p>
                                                                <p className={`text-lg ${item.co2e < 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                                    {item.co2e > 0 ? '+' : ''}{item.co2e.toFixed(2)} tCO₂e
                                                                </p>
                                                            </div>
                                                            <div className="text-center">
                                                                <p className="text-xs text-gray-600 mb-1">Waste Reduction</p>
                                                                <p className={`text-lg ${item.waste < 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                                    {item.waste > 0 ? '+' : ''}{item.waste.toFixed(2)} kg
                                                                </p>
                                                            </div>
                                                            <div className="text-center">
                                                                <p className="text-xs text-gray-600 mb-1">Trees Impact</p>
                                                                <p className={`text-lg ${item.treesSaved > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                                    {item.treesSaved > 0 ? '+' : ''}{item.treesSaved.toFixed(2)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                <div>
                                                    <Label htmlFor={`desc-${item.id}`}>Description</Label>
                                                    <Textarea
                                                        id={`desc-${item.id}`}
                                                        value={item.description}
                                                        onChange={(e) =>
                                                            handleUpdateGraphItem(item.id, 'description', e.target.value)
                                                        }
                                                        rows={2}
                                                        placeholder="Brief description of this data point"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* Certificates Section */}
                        {activeSection === 'certificates' && (
                            <div className="space-y-6">
                                {/* Statistics Overview */}
                                <div className="grid grid-cols-3 gap-4">
                                    <Card>
                                        <CardContent className="pt-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Total Certificates</p>
                                                    <p className="text-2xl text-gray-900 mt-1">{certificates.length}</p>
                                                </div>
                                                <FileText className="w-10 h-10 text-[#0072CE] opacity-20" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="pt-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Added This Year</p>
                                                    <p className="text-2xl text-gray-900 mt-1">
                                                        {certificates.filter(c => c.date?.includes('2025')).length}
                                                    </p>
                                                </div>
                                                <Calendar className="w-10 h-10 text-green-500 opacity-20" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="pt-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Documents</p>
                                                    <p className="text-2xl text-gray-900 mt-1">{certificates.length}</p>
                                                </div>
                                                <Award className="w-10 h-10 text-amber-500 opacity-20" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Add New Certificate */}
                                <Card className="border-2 border-dashed border-gray-300 hover:border-[#0072CE] transition-colors">
                                    <CardHeader>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-[#0072CE] rounded-lg flex items-center justify-center">
                                                <Plus className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <CardTitle>Add New Certificate</CardTitle>
                                                <CardDescription>
                                                    Upload and add certificates to the customer's library
                                                </CardDescription>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        {/* File Upload Simulation */}
                                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#0072CE] transition-colors cursor-pointer bg-gray-50">
                                            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                            <p className="text-sm text-gray-700 mb-1">
                                                Click to upload or drag and drop
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                PDF, PNG, JPG up to 10MB
                                            </p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="cert-title" className="flex items-center gap-2">
                                                    <FileText className="w-4 h-4 text-gray-500" />
                                                    Certificate Title
                                                </Label>
                                                <Input
                                                    id="cert-title"
                                                    value={newCertificate.title}
                                                    onChange={(e) =>
                                                        setNewCertificate({ ...newCertificate, title: e.target.value })
                                                    }
                                                    placeholder="e.g., CHEP Scope 3 Emissions Factor"
                                                    className="mt-1"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="cert-date" className="flex items-center gap-2">
                                                    <Calendar className="w-4 h-4 text-gray-500" />
                                                    Issue Date
                                                </Label>
                                                <Input
                                                    id="cert-date"
                                                    value={newCertificate.date}
                                                    onChange={(e) =>
                                                        setNewCertificate({ ...newCertificate, date: e.target.value })
                                                    }
                                                    placeholder="e.g., January 2025"
                                                    className="mt-1"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <Label htmlFor="cert-description" className="flex items-center gap-2">
                                                <Edit2 className="w-4 h-4 text-gray-500" />
                                                Description
                                            </Label>
                                            <Textarea
                                                id="cert-description"
                                                value={newCertificate.description}
                                                onChange={(e) =>
                                                    setNewCertificate({ ...newCertificate, description: e.target.value })
                                                }
                                                placeholder="Brief description of the certificate and its purpose..."
                                                rows={3}
                                                className="mt-1"
                                            />
                                        </div>

                                        <div className="flex gap-3">
                                            <Button
                                                onClick={handleAddCertificate}
                                                className="gap-2 flex-1"
                                                disabled={!newCertificate.title || !newCertificate.description}
                                            >
                                                <Plus className="w-4 h-4" />
                                                Add Certificate
                                            </Button>
                                            <Button
                                                variant="outline"
                                                onClick={() => setNewCertificate({ title: '', description: '', date: '' })}
                                            >
                                                Clear
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Existing Certificates */}
                                <Card>
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <CardTitle>Certificate Library</CardTitle>
                                                <CardDescription>
                                                    Manage and edit existing certificates
                                                </CardDescription>
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                {certificates.length} {certificates.length === 1 ? 'item' : 'items'}
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        {certificates.length === 0 ? (
                                            <div className="text-center py-12">
                                                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                                <p className="text-gray-600 mb-2">No certificates added yet</p>
                                                <p className="text-sm text-gray-500">
                                                    Add your first certificate using the form above
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                {certificates.map((cert, index) => (
                                                    <div
                                                        key={cert.id}
                                                        className="group p-5 border-2 border-gray-200 rounded-lg hover:border-[#0072CE] hover:shadow-md transition-all bg-white"
                                                    >
                                                        <div className="flex items-start gap-4">
                                                            {/* Position Controls */}
                                                            <div className="flex flex-col gap-1">
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handleMoveCertificate(index, 'up')}
                                                                    disabled={index === 0}
                                                                    className="h-8 w-8 p-0"
                                                                    title="Move up"
                                                                >
                                                                    <ChevronUp className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handleMoveCertificate(index, 'down')}
                                                                    disabled={index === certificates.length - 1}
                                                                    className="h-8 w-8 p-0"
                                                                    title="Move down"
                                                                >
                                                                    <ChevronDown className="w-4 h-4" />
                                                                </Button>
                                                            </div>

                                                            {/* Certificate Icon */}
                                                            <div className="w-12 h-12 bg-gradient-to-br from-[#0072CE] to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                                                <FileText className="w-6 h-6 text-white" />
                                                            </div>

                                                            {/* Certificate Content */}
                                                            <div className="flex-1 space-y-3">
                                                                <div className="flex items-start justify-between gap-4">
                                                                    <div className="flex-1 space-y-2">
                                                                        <Input
                                                                            value={cert.title}
                                                                            onChange={(e) => handleUpdateCertificate(cert.id, 'title', e.target.value)}
                                                                            className="text-base border-gray-200 focus:border-[#0072CE]"
                                                                            placeholder="Certificate title"
                                                                        />
                                                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                                                            <Calendar className="w-4 h-4" />
                                                                            <Input
                                                                                value={cert.date || ''}
                                                                                onChange={(e) => handleUpdateCertificate(cert.id, 'date', e.target.value)}
                                                                                placeholder="Issue date"
                                                                                className="w-40 h-8 text-sm"
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    {/* Action Buttons */}
                                                                    <div className="flex gap-2">
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                        >
                                                                            <Download className="w-4 h-4" />
                                                                            Download
                                                                        </Button>
                                                                        <Button
                                                                            variant="destructive"
                                                                            size="sm"
                                                                            onClick={() => handleDeleteCertificate(cert.id)}
                                                                            className="gap-2"
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                            Delete
                                                                        </Button>
                                                                    </div>
                                                                </div>

                                                                <Textarea
                                                                    value={cert.description}
                                                                    onChange={(e) =>
                                                                        handleUpdateCertificate(cert.id, 'description', e.target.value)
                                                                    }
                                                                    rows={2}
                                                                    className="text-sm border-gray-200 focus:border-[#0072CE]"
                                                                    placeholder="Certificate description..."
                                                                />

                                                                {/* Certificate Metadata */}
                                                                <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                                                                    <span className="flex items-center gap-1">
                                                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                                        Active
                                                                    </span>
                                                                    <span>ID: {cert.id}</span>
                                                                    <span>Position: #{index + 1}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* CE University Section */}
                        {activeSection === 'ce-university' && (
                            <div className="space-y-6">
                                {/* Statistics Overview */}
                                <div className="grid grid-cols-4 gap-4">
                                    <Card>
                                        <CardContent className="pt-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Total Content</p>
                                                    <p className="text-2xl text-gray-900 mt-1">
                                                        {(ceData.caseStudies?.length || 0) + (ceData.videos?.length || 0) + (ceData.webinars?.length || 0)}
                                                    </p>
                                                </div>
                                                <GraduationCap className="w-10 h-10 text-[#0072CE] opacity-20" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="pt-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Case Studies</p>
                                                    <p className="text-2xl text-gray-900 mt-1">{ceData.caseStudies?.length || 0}</p>
                                                </div>
                                                <FileText className="w-10 h-10 text-blue-500 opacity-20" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="pt-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Videos</p>
                                                    <p className="text-2xl text-gray-900 mt-1">{ceData.videos?.length || 0}</p>
                                                </div>
                                                <BarChart3 className="w-10 h-10 text-purple-500 opacity-20" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="pt-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Webinars</p>
                                                    <p className="text-2xl text-gray-900 mt-1">{ceData.webinars?.length || 0}</p>
                                                </div>
                                                <Award className="w-10 h-10 text-green-500 opacity-20" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Add New Content */}
                                <Card className="border-2 border-dashed border-gray-300 hover:border-[#0072CE] transition-colors">
                                    <CardHeader>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-[#0072CE] rounded-lg flex items-center justify-center">
                                                <Plus className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <CardTitle>Add New CE University Content</CardTitle>
                                                <CardDescription>
                                                    Create educational content for case studies, videos, or webinars
                                                </CardDescription>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        {/* Category Selection with Visual Cards */}
                                        <div>
                                            <Label className="flex items-center gap-2 mb-3">
                                                <GraduationCap className="w-4 h-4 text-gray-500" />
                                                Content Type
                                            </Label>
                                            <div className="grid grid-cols-3 gap-3">
                                                <button
                                                    onClick={() => setCECategory('caseStudies')}
                                                    className={`p-4 border-2 rounded-lg transition-all ${ceCategory === 'caseStudies'
                                                        ? 'border-[#0072CE] bg-blue-50'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                        }`}
                                                >
                                                    <FileText className={`w-8 h-8 mx-auto mb-2 ${ceCategory === 'caseStudies' ? 'text-[#0072CE]' : 'text-gray-400'
                                                        }`} />
                                                    <p className="text-sm text-gray-900">Case Studies</p>
                                                </button>
                                                <button
                                                    onClick={() => setCECategory('videos')}
                                                    className={`p-4 border-2 rounded-lg transition-all ${ceCategory === 'videos'
                                                        ? 'border-purple-500 bg-purple-50'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                        }`}
                                                >
                                                    <BarChart3 className={`w-8 h-8 mx-auto mb-2 ${ceCategory === 'videos' ? 'text-purple-500' : 'text-gray-400'
                                                        }`} />
                                                    <p className="text-sm text-gray-900">Videos</p>
                                                </button>
                                                <button
                                                    onClick={() => setCECategory('webinars')}
                                                    className={`p-4 border-2 rounded-lg transition-all ${ceCategory === 'webinars'
                                                        ? 'border-green-500 bg-green-50'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                        }`}
                                                >
                                                    <Award className={`w-8 h-8 mx-auto mb-2 ${ceCategory === 'webinars' ? 'text-green-500' : 'text-gray-400'
                                                        }`} />
                                                    <p className="text-sm text-gray-900">Webinars</p>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="ce-title" className="flex items-center gap-2">
                                                    <FileText className="w-4 h-4 text-gray-500" />
                                                    Title
                                                </Label>
                                                <Input
                                                    id="ce-title"
                                                    value={newCEItem.title}
                                                    onChange={(e) =>
                                                        setNewCEItem({ ...newCEItem, title: e.target.value })
                                                    }
                                                    placeholder="e.g., Introduction to Circular Economy"
                                                    className="mt-1"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="ce-duration" className="flex items-center gap-2">
                                                    <Calendar className="w-4 h-4 text-gray-500" />
                                                    Duration (optional)
                                                </Label>
                                                <Input
                                                    id="ce-duration"
                                                    value={newCEItem.duration}
                                                    onChange={(e) =>
                                                        setNewCEItem({ ...newCEItem, duration: e.target.value })
                                                    }
                                                    placeholder="e.g., 15 min"
                                                    className="mt-1"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <Label htmlFor="ce-description" className="flex items-center gap-2">
                                                <Edit2 className="w-4 h-4 text-gray-500" />
                                                Description
                                            </Label>
                                            <Textarea
                                                id="ce-description"
                                                value={newCEItem.description}
                                                onChange={(e) =>
                                                    setNewCEItem({ ...newCEItem, description: e.target.value })
                                                }
                                                placeholder="Detailed description of the content and learning objectives..."
                                                rows={3}
                                                className="mt-1"
                                            />
                                        </div>

                                        <div>
                                            <Label htmlFor="ce-tag" className="flex items-center gap-2">
                                                <Award className="w-4 h-4 text-gray-500" />
                                                Category Tag
                                            </Label>
                                            <Input
                                                id="ce-tag"
                                                value={newCEItem.category}
                                                onChange={(e) =>
                                                    setNewCEItem({ ...newCEItem, category: e.target.value })
                                                }
                                                placeholder="e.g., EPR Webinar, Sustainability, Best Practices"
                                                className="mt-1"
                                            />
                                        </div>

                                        <div className="flex gap-3">
                                            <Button
                                                onClick={handleAddCEItem}
                                                className="gap-2 flex-1"
                                                disabled={!newCEItem.title || !newCEItem.description}
                                            >
                                                <Plus className="w-4 h-4" />
                                                Add {ceCategory === 'caseStudies' ? 'Case Study' : ceCategory === 'videos' ? 'Video' : 'Webinar'}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                onClick={() => setNewCEItem({ title: '', description: '', duration: '', category: '' })}
                                            >
                                                Clear
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Case Studies */}
                                <Card>
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                                    <FileText className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <div>
                                                    <CardTitle>Case Studies</CardTitle>
                                                    <CardDescription>Educational case studies and success stories</CardDescription>
                                                </div>
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                {ceData.caseStudies?.length || 0} {ceData.caseStudies?.length === 1 ? 'item' : 'items'}
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        {(!ceData.caseStudies || ceData.caseStudies.length === 0) ? (
                                            <div className="text-center py-12">
                                                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                                <p className="text-gray-600 mb-2">No case studies added yet</p>
                                                <p className="text-sm text-gray-500">
                                                    Add your first case study using the form above
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                {ceData.caseStudies.map((item: any, index: number) => (
                                                    <div key={item.id} className="group p-5 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:shadow-md transition-all bg-white">
                                                        <div className="flex items-start gap-4">
                                                            {/* Position Controls */}
                                                            <div className="flex flex-col gap-1">
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handleMoveCEItem('caseStudies', index, 'up')}
                                                                    disabled={index === 0}
                                                                    className="h-8 w-8 p-0"
                                                                    title="Move up"
                                                                >
                                                                    <ChevronUp className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handleMoveCEItem('caseStudies', index, 'down')}
                                                                    disabled={index === ceData.caseStudies.length - 1}
                                                                    className="h-8 w-8 p-0"
                                                                    title="Move down"
                                                                >
                                                                    <ChevronDown className="w-4 h-4" />
                                                                </Button>
                                                            </div>

                                                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                                                <FileText className="w-6 h-6 text-white" />
                                                            </div>
                                                            <div className="flex-1 space-y-2">
                                                                <div className="flex items-start justify-between gap-4">
                                                                    <div className="flex-1">
                                                                        <h4 className="text-gray-900">{item.title}</h4>
                                                                        {item.duration && (
                                                                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                                                                                <Calendar className="w-3 h-3" />
                                                                                {item.duration}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                    <Button
                                                                        variant="destructive"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteCEItem('caseStudies', item.id)}
                                                                        className="gap-2"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                        Delete
                                                                    </Button>
                                                                </div>
                                                                <p className="text-sm text-gray-700">{item.description}</p>
                                                                {item.category && (
                                                                    <div className="flex items-center gap-2 pt-2">
                                                                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                                                                            {item.category}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                                                                    <span className="flex items-center gap-1">
                                                                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                                                        Active
                                                                    </span>
                                                                    <span>Position: #{index + 1}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Videos */}
                                <Card>
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                                    <BarChart3 className="w-5 h-5 text-purple-600" />
                                                </div>
                                                <div>
                                                    <CardTitle>Circular Economy Videos</CardTitle>
                                                    <CardDescription>Educational video content and tutorials</CardDescription>
                                                </div>
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                {ceData.videos?.length || 0} {ceData.videos?.length === 1 ? 'item' : 'items'}
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        {(!ceData.videos || ceData.videos.length === 0) ? (
                                            <div className="text-center py-12">
                                                <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                                <p className="text-gray-600 mb-2">No videos added yet</p>
                                                <p className="text-sm text-gray-500">
                                                    Add your first video using the form above
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                {ceData.videos.map((item: any, index: number) => (
                                                    <div key={item.id} className="group p-5 border-2 border-gray-200 rounded-lg hover:border-purple-400 hover:shadow-md transition-all bg-white">
                                                        <div className="flex items-start gap-4">
                                                            {/* Position Controls */}
                                                            <div className="flex flex-col gap-1">
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handleMoveCEItem('videos', index, 'up')}
                                                                    disabled={index === 0}
                                                                    className="h-8 w-8 p-0"
                                                                    title="Move up"
                                                                >
                                                                    <ChevronUp className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handleMoveCEItem('videos', index, 'down')}
                                                                    disabled={index === ceData.videos.length - 1}
                                                                    className="h-8 w-8 p-0"
                                                                    title="Move down"
                                                                >
                                                                    <ChevronDown className="w-4 h-4" />
                                                                </Button>
                                                            </div>

                                                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                                                <BarChart3 className="w-6 h-6 text-white" />
                                                            </div>
                                                            <div className="flex-1 space-y-2">
                                                                <div className="flex items-start justify-between gap-4">
                                                                    <div className="flex-1">
                                                                        <h4 className="text-gray-900">{item.title}</h4>
                                                                        {item.duration && (
                                                                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                                                                                <Calendar className="w-3 h-3" />
                                                                                {item.duration}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                    <Button
                                                                        variant="destructive"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteCEItem('videos', item.id)}
                                                                        className="gap-2"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                        Delete
                                                                    </Button>
                                                                </div>
                                                                <p className="text-sm text-gray-700">{item.description}</p>
                                                                {item.category && (
                                                                    <div className="flex items-center gap-2 pt-2">
                                                                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                                                                            {item.category}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                                                                    <span className="flex items-center gap-1">
                                                                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                                                        Active
                                                                    </span>
                                                                    <span>Position: #{index + 1}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Webinars */}
                                <Card>
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                                    <Award className="w-5 h-5 text-green-600" />
                                                </div>
                                                <div>
                                                    <CardTitle>Webinars</CardTitle>
                                                    <CardDescription>Live and recorded webinar sessions</CardDescription>
                                                </div>
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                {ceData.webinars?.length || 0} {ceData.webinars?.length === 1 ? 'item' : 'items'}
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        {(!ceData.webinars || ceData.webinars.length === 0) ? (
                                            <div className="text-center py-12">
                                                <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                                <p className="text-gray-600 mb-2">No webinars added yet</p>
                                                <p className="text-sm text-gray-500">
                                                    Add your first webinar using the form above
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                {ceData.webinars.map((item: any, index: number) => (
                                                    <div key={item.id} className="group p-5 border-2 border-gray-200 rounded-lg hover:border-green-400 hover:shadow-md transition-all bg-white">
                                                        <div className="flex items-start gap-4">
                                                            {/* Position Controls */}
                                                            <div className="flex flex-col gap-1">
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handleMoveCEItem('webinars', index, 'up')}
                                                                    disabled={index === 0}
                                                                    className="h-8 w-8 p-0"
                                                                    title="Move up"
                                                                >
                                                                    <ChevronUp className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handleMoveCEItem('webinars', index, 'down')}
                                                                    disabled={index === ceData.webinars.length - 1}
                                                                    className="h-8 w-8 p-0"
                                                                    title="Move down"
                                                                >
                                                                    <ChevronDown className="w-4 h-4" />
                                                                </Button>
                                                            </div>

                                                            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                                                <Award className="w-6 h-6 text-white" />
                                                            </div>
                                                            <div className="flex-1 space-y-2">
                                                                <div className="flex items-start justify-between gap-4">
                                                                    <div className="flex-1">
                                                                        <h4 className="text-gray-900">{item.title}</h4>
                                                                        {item.duration && (
                                                                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                                                                                <Calendar className="w-3 h-3" />
                                                                                {item.duration}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                    <Button
                                                                        variant="destructive"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteCEItem('webinars', item.id)}
                                                                        className="gap-2"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                        Delete
                                                                    </Button>
                                                                </div>
                                                                <p className="text-sm text-gray-700">{item.description}</p>
                                                                {item.category && (
                                                                    <div className="flex items-center gap-2 pt-2">
                                                                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                                                                            {item.category}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                                                                    <span className="flex items-center gap-1">
                                                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                                        Active
                                                                    </span>
                                                                    <span>Position: #{index + 1}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* Certificate Preview Section */}
                        {activeSection === 'download-cert' && (
                            <div className="space-y-6">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Certificate Preview Settings</CardTitle>
                                        <CardDescription>
                                            Configure the downloadable certificate preview card
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg text-center">
                                            <Award className="w-12 h-12 text-[#0072CE] mx-auto mb-4" />
                                            <p className="text-gray-700">
                                                The certificate preview is generated from the uploaded image.
                                            </p>
                                            <p className="text-sm text-gray-600 mt-2">
                                                To update the certificate image, replace the certificate image file in your assets.
                                            </p>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* Suppliers Section */}
                        {activeSection === 'suppliers' && (
                            <div className="space-y-6">
                                {/* Statistics Overview */}
                                <div className="grid grid-cols-4 gap-4">
                                    <Card>
                                        <CardContent className="pt-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Total Suppliers</p>
                                                    <p className="text-2xl text-gray-900 mt-1">{suppliers.length}</p>
                                                </div>
                                                <Users className="w-10 h-10 text-[#0072CE] opacity-20" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="pt-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Integrated</p>
                                                    <p className="text-2xl text-green-600 mt-1">
                                                        {suppliers.filter((s: any) => s.status === 'integrated').length}
                                                    </p>
                                                </div>
                                                <Building2 className="w-10 h-10 text-green-500 opacity-20" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="pt-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Non-Integrated</p>
                                                    <p className="text-2xl text-orange-600 mt-1">
                                                        {suppliers.filter((s: any) => s.status === 'non-integrated').length}
                                                    </p>
                                                </div>
                                                <Building2 className="w-10 h-10 text-orange-500 opacity-20" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="pt-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Potential Impact</p>
                                                    <p className="text-2xl text-blue-600 mt-1">
                                                        {suppliers.filter((s: any) => s.status === 'non-integrated').reduce((acc: number, s: any) => acc + (s.co2eImpact || 0), 0).toFixed(0)}
                                                    </p>
                                                </div>
                                                <BarChart3 className="w-10 h-10 text-blue-500 opacity-20" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Add New Supplier */}
                                <Card className="border-2 border-dashed border-gray-300 hover:border-[#0072CE] transition-colors">
                                    <CardHeader>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-[#0072CE] rounded-lg flex items-center justify-center">
                                                <Plus className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <CardTitle>Add New Supplier</CardTitle>
                                                <CardDescription>
                                                    Add new suppliers to the customer's list
                                                </CardDescription>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="supplier-name" className="flex items-center gap-2">
                                                    <Building2 className="w-4 h-4 text-gray-500" />
                                                    Supplier Name
                                                </Label>
                                                <Input
                                                    id="supplier-name"
                                                    value={newSupplier.name}
                                                    onChange={(e) =>
                                                        setNewSupplier({ ...newSupplier, name: e.target.value })
                                                    }
                                                    placeholder="e.g., GreenTech Solutions"
                                                    className="mt-1"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="supplier-contact" className="flex items-center gap-2">
                                                    <Users className="w-4 h-4 text-gray-500" />
                                                    Contact Person
                                                </Label>
                                                <Input
                                                    id="supplier-contact"
                                                    value={newSupplier.contact}
                                                    onChange={(e) =>
                                                        setNewSupplier({ ...newSupplier, contact: e.target.value })
                                                    }
                                                    placeholder="e.g., John Doe"
                                                    className="mt-1"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="supplier-email" className="flex items-center gap-2">
                                                    <Mail className="w-4 h-4 text-gray-500" />
                                                    Email
                                                </Label>
                                                <Input
                                                    id="supplier-email"
                                                    value={newSupplier.email}
                                                    onChange={(e) =>
                                                        setNewSupplier({ ...newSupplier, email: e.target.value })
                                                    }
                                                    placeholder="e.g., john.doe@example.com"
                                                    className="mt-1"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="supplier-phone" className="flex items-center gap-2">
                                                    <Phone className="w-4 h-4 text-gray-500" />
                                                    Phone
                                                </Label>
                                                <Input
                                                    id="supplier-phone"
                                                    value={newSupplier.phone}
                                                    onChange={(e) =>
                                                        setNewSupplier({ ...newSupplier, phone: e.target.value })
                                                    }
                                                    placeholder="e.g., +1 123 456 7890"
                                                    className="mt-1"
                                                />
                                            </div>
                                        </div>

                                        {/* Integration Status */}
                                        <div>
                                            <Label className="flex items-center gap-2 mb-3">
                                                <Building2 className="w-4 h-4 text-gray-500" />
                                                Integration Status
                                            </Label>
                                            <div className="grid grid-cols-2 gap-3">
                                                <button
                                                    onClick={() => setNewSupplier({ ...newSupplier, status: 'integrated' })}
                                                    className={`p-4 border-2 rounded-lg transition-all ${newSupplier.status === 'integrated'
                                                        ? 'border-green-500 bg-green-50'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                        }`}
                                                >
                                                    <div className={`w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center ${newSupplier.status === 'integrated' ? 'bg-green-500' : 'bg-gray-300'
                                                        }`}>
                                                        <Building2 className="w-4 h-4 text-white" />
                                                    </div>
                                                    <p className="text-sm text-gray-900">Integrated</p>
                                                    <p className="text-xs text-gray-500 mt-1">Currently using CHEP</p>
                                                </button>
                                                <button
                                                    onClick={() => setNewSupplier({ ...newSupplier, status: 'non-integrated' })}
                                                    className={`p-4 border-2 rounded-lg transition-all ${newSupplier.status === 'non-integrated'
                                                        ? 'border-orange-500 bg-orange-50'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                        }`}
                                                >
                                                    <div className={`w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center ${newSupplier.status === 'non-integrated' ? 'bg-orange-500' : 'bg-gray-300'
                                                        }`}>
                                                        <Building2 className="w-4 h-4 text-white" />
                                                    </div>
                                                    <p className="text-sm text-gray-900">Non-Integrated</p>
                                                    <p className="text-xs text-gray-500 mt-1">Potential opportunity</p>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Impact Metrics - Only for Non-Integrated */}
                                        {newSupplier.status === 'non-integrated' && (
                                            <div className="p-4 bg-orange-50 border-2 border-orange-200 rounded-lg space-y-4">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <BarChart3 className="w-5 h-5 text-orange-600" />
                                                    <h4 className="text-sm text-gray-900">Potential Impact if Integrated</h4>
                                                </div>
                                                <p className="text-xs text-gray-600 mb-3">
                                                    Define the potential environmental impact if this supplier integrates with CHEP. These values will be shown as lighter bars on the dashboard.
                                                </p>
                                                <div className="grid grid-cols-3 gap-4">
                                                    <div>
                                                        <Label htmlFor="co2e-impact" className="text-xs">
                                                            CO₂e Impact (tCO₂e)
                                                        </Label>
                                                        <Input
                                                            id="co2e-impact"
                                                            type="number"
                                                            value={newSupplier.co2eImpact}
                                                            onChange={(e) =>
                                                                setNewSupplier({ ...newSupplier, co2eImpact: parseFloat(e.target.value) || 0 })
                                                            }
                                                            placeholder="0"
                                                            className="mt-1"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label htmlFor="waste-impact" className="text-xs">
                                                            Waste Impact (kg)
                                                        </Label>
                                                        <Input
                                                            id="waste-impact"
                                                            type="number"
                                                            value={newSupplier.wasteImpact}
                                                            onChange={(e) =>
                                                                setNewSupplier({ ...newSupplier, wasteImpact: parseFloat(e.target.value) || 0 })
                                                            }
                                                            placeholder="0"
                                                            className="mt-1"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label htmlFor="trees-impact" className="text-xs">
                                                            Trees Impact
                                                        </Label>
                                                        <Input
                                                            id="trees-impact"
                                                            type="number"
                                                            value={newSupplier.treesImpact}
                                                            onChange={(e) =>
                                                                setNewSupplier({ ...newSupplier, treesImpact: parseFloat(e.target.value) || 0 })
                                                            }
                                                            placeholder="0"
                                                            className="mt-1"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex gap-3">
                                            <Button
                                                onClick={handleAddSupplier}
                                                className="gap-2 flex-1"
                                                disabled={!newSupplier.name || !newSupplier.contact || !newSupplier.email || !newSupplier.phone}
                                            >
                                                <Plus className="w-4 h-4" />
                                                Add Supplier
                                            </Button>
                                            <Button
                                                variant="outline"
                                                onClick={() => setNewSupplier({
                                                    name: '',
                                                    contact: '',
                                                    email: '',
                                                    phone: '',
                                                    status: 'integrated' as 'integrated' | 'non-integrated',
                                                    co2eImpact: 0,
                                                    wasteImpact: 0,
                                                    treesImpact: 0,
                                                })}
                                            >
                                                Clear
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Existing Suppliers */}
                                <Card>
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <CardTitle>Supplier List</CardTitle>
                                                <CardDescription>
                                                    Manage and edit existing suppliers
                                                </CardDescription>
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                {suppliers.length} {suppliers.length === 1 ? 'item' : 'items'}
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        {suppliers.length === 0 ? (
                                            <div className="text-center py-12">
                                                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                                <p className="text-gray-600 mb-2">No suppliers added yet</p>
                                                <p className="text-sm text-gray-500">
                                                    Add your first supplier using the form above
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                {suppliers.map((supplier: Supplier, index: number) => (
                                                    <div
                                                        key={supplier.id}
                                                        className="group p-5 border-2 border-gray-200 rounded-lg hover:border-[#0072CE] hover:shadow-md transition-all bg-white"
                                                    >
                                                        <div className="flex items-start gap-4">
                                                            {/* Supplier Icon */}
                                                            <div className="w-12 h-12 bg-gradient-to-br from-[#0072CE] to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                                                <Building2 className="w-6 h-6 text-white" />
                                                            </div>

                                                            {/* Supplier Content */}
                                                            <div className="flex-1 space-y-3">
                                                                <div className="flex items-start justify-between gap-4">
                                                                    <div className="flex-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <h4 className="text-gray-900">{supplier.name}</h4>
                                                                            <span className={`px-2 py-1 rounded-full text-xs ${supplier.status === 'integrated'
                                                                                ? 'bg-green-100 text-green-700'
                                                                                : 'bg-orange-100 text-orange-700'
                                                                                }`}>
                                                                                {supplier.status === 'integrated' ? 'Integrated' : 'Non-Integrated'}
                                                                            </span>
                                                                        </div>
                                                                        <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                                                                            <Users className="w-3 h-3" />
                                                                            {supplier.contact}
                                                                        </p>
                                                                    </div>

                                                                    {/* Action Buttons */}
                                                                    <div className="flex gap-2">
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                const newStatus = supplier.status === 'integrated' ? 'non-integrated' : 'integrated';
                                                                                handleUpdateSupplier(supplier.id, 'status', newStatus);
                                                                            }}
                                                                            className="gap-2"
                                                                        >
                                                                            <Building2 className="w-4 h-4" />
                                                                            Toggle Status
                                                                        </Button>
                                                                        <Button
                                                                            variant="destructive"
                                                                            size="sm"
                                                                            onClick={() => handleDeleteSupplier(supplier.id)}
                                                                            className="gap-2"
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                            Delete
                                                                        </Button>
                                                                    </div>
                                                                </div>

                                                                <div className="grid grid-cols-2 gap-2">
                                                                    <p className="text-sm text-gray-700 flex items-center gap-1">
                                                                        <Mail className="w-3 h-3" />
                                                                        {supplier.email}
                                                                    </p>
                                                                    <p className="text-sm text-gray-700 flex items-center gap-1">
                                                                        <Phone className="w-3 h-3" />
                                                                        {supplier.phone}
                                                                    </p>
                                                                </div>

                                                                {/* Impact Metrics for Non-Integrated Suppliers */}
                                                                {supplier.status === 'non-integrated' && (
                                                                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                                                        <div className="flex items-center gap-2 mb-2">
                                                                            <BarChart3 className="w-4 h-4 text-orange-600" />
                                                                            <h5 className="text-xs text-gray-900">Potential Impact if Integrated</h5>
                                                                        </div>
                                                                        <div className="grid grid-cols-3 gap-3">
                                                                            <div>
                                                                                <Label htmlFor={`co2e-${supplier.id}`} className="text-xs text-gray-600">
                                                                                    CO₂e (tCO₂e)
                                                                                </Label>
                                                                                <Input
                                                                                    id={`co2e-${supplier.id}`}
                                                                                    type="number"
                                                                                    value={supplier.co2eImpact || 0}
                                                                                    onChange={(e) =>
                                                                                        handleUpdateSupplier(supplier.id, 'co2eImpact', parseFloat(e.target.value) || 0)
                                                                                    }
                                                                                    className="h-8 text-sm mt-1"
                                                                                />
                                                                            </div>
                                                                            <div>
                                                                                <Label htmlFor={`waste-${supplier.id}`} className="text-xs text-gray-600">
                                                                                    Waste (kg)
                                                                                </Label>
                                                                                <Input
                                                                                    id={`waste-${supplier.id}`}
                                                                                    type="number"
                                                                                    value={supplier.wasteImpact || 0}
                                                                                    onChange={(e) =>
                                                                                        handleUpdateSupplier(supplier.id, 'wasteImpact', parseFloat(e.target.value) || 0)
                                                                                    }
                                                                                    className="h-8 text-sm mt-1"
                                                                                />
                                                                            </div>
                                                                            <div>
                                                                                <Label htmlFor={`trees-${supplier.id}`} className="text-xs text-gray-600">
                                                                                    Trees
                                                                                </Label>
                                                                                <Input
                                                                                    id={`trees-${supplier.id}`}
                                                                                    type="number"
                                                                                    value={supplier.treesImpact || 0}
                                                                                    onChange={(e) =>
                                                                                        handleUpdateSupplier(supplier.id, 'treesImpact', parseFloat(e.target.value) || 0)
                                                                                    }
                                                                                    className="h-8 text-sm mt-1"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Supplier Metadata */}
                                                                <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                                                                    <span className="flex items-center gap-1">
                                                                        <div className={`w-2 h-2 rounded-full ${supplier.status === 'integrated' ? 'bg-green-500' : 'bg-orange-500'
                                                                            }`}></div>
                                                                        Active
                                                                    </span>
                                                                    <span>ID: {supplier.id}</span>
                                                                    <span>Position: #{index + 1}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
